/**
 * web/src/components/ui/dialog.tsx
 * Radix Dialog wrapper with token-overridden styling matching the
 * design handoff `.modal` contract (styles.css lines 1224-1272).
 * 480px width, 10px radius, shadow-pop, backdrop rgba(9,9,11,0.4)
 * with 150ms fade. Modal entrance: 180ms cubic-bezier pop-in.
 *
 * Pages MUST import Dialog from this wrapper, not from @radix-ui/react-dialog
 * directly — keeps the visual contract centralized so every modal in the
 * app inherits the same backdrop, animation timing, and modal frame.
 */
import { forwardRef } from 'react';
import type { ComponentPropsWithoutRef, ElementRef, ReactNode } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { cn } from '../../lib/cn';

/** Root context provider — controls open state. */
export const Dialog = DialogPrimitive.Root;

/** Element that toggles the dialog open. Wraps a button (asChild) or renders one. */
export const DialogTrigger = DialogPrimitive.Trigger;

/** Portals the dialog to document.body so it escapes parent overflow/transform. */
export const DialogPortal = DialogPrimitive.Portal;

/** Element that closes the dialog when clicked. */
export const DialogClose = DialogPrimitive.Close;

/**
 * Backdrop overlay rendered behind the modal. Matches `.modal-backdrop`:
 * full-bleed fixed positioning, rgba(9,9,11,0.4) tint, 150ms fade-in keyframe
 * from index.css. Closing fades out via opacity transition.
 */
export const DialogOverlay = forwardRef<
  ElementRef<typeof DialogPrimitive.Overlay>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-50 bg-[rgba(9,9,11,0.4)]',
      'data-[state=open]:animate-[fade-in_150ms_ease-out]',
      'data-[state=closed]:opacity-0 data-[state=closed]:transition-opacity data-[state=closed]:duration-150',
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = 'DialogOverlay';

/**
 * Modal frame matching `.modal`: 480px wide (max-w-[calc(100vw-48px)]),
 * white bg, 10px radius (rounded-xl == --r-4), shadow-pop, overflow hidden,
 * pop-in animation on open and opacity fade on close. Centered via
 * left/top 50% + translate -50%.
 */
export const DialogContent = forwardRef<
  ElementRef<typeof DialogPrimitive.Content>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        'fixed left-1/2 top-1/2 z-50 w-[480px] max-w-[calc(100vw-48px)] -translate-x-1/2 -translate-y-1/2',
        'bg-bg rounded-xl shadow-pop overflow-hidden',
        'data-[state=open]:animate-[pop-in_180ms_cubic-bezier(.2,.9,.3,1.2)]',
        'data-[state=closed]:opacity-0 data-[state=closed]:transition-opacity data-[state=closed]:duration-150',
        className,
      )}
      {...props}
    >
      {children}
    </DialogPrimitive.Content>
  </DialogPortal>
));
DialogContent.displayName = 'DialogContent';

/**
 * Optional header region matching `.modal-head`: 16/20px padding (px-5 pt-4 pb-2).
 * Holds the DialogTitle and DialogDescription.
 */
export function DialogHeader({
  className,
  children,
}: {
  className?: string;
  children?: ReactNode;
}) {
  return <div className={cn('px-5 pt-4 pb-2', className)}>{children}</div>;
}

/**
 * Title element matching `.modal-title`: 15px / weight 600 / -0.01em tracking.
 * Mapped to Radix's a11y-required Title slot so screen readers announce.
 */
export const DialogTitle = forwardRef<
  ElementRef<typeof DialogPrimitive.Title>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('text-[15px] font-semibold -tracking-[0.01em] text-text', className)}
    {...props}
  />
));
DialogTitle.displayName = 'DialogTitle';

/** Description: 13px muted text. Mapped to Radix's a11y Description slot. */
export const DialogDescription = forwardRef<
  ElementRef<typeof DialogPrimitive.Description>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-[13px] text-text-muted', className)}
    {...props}
  />
));
DialogDescription.displayName = 'DialogDescription';

/**
 * Body region matching `.modal-body`: 8/20px padding, 13px muted, 1.55 leading.
 * Used for the descriptive copy + form contents inside the modal.
 */
export function DialogBody({ className, children }: { className?: string; children?: ReactNode }) {
  return (
    <div className={cn('px-5 pb-4 pt-2 text-[13px] text-text-muted', className)}>{children}</div>
  );
}

/**
 * Footer region matching `.modal-foot`: 12/20px padding, bg-soft, 1px top
 * border, right-aligned button row with 8px gap.
 */
export function DialogFooter({
  className,
  children,
}: {
  className?: string;
  children?: ReactNode;
}) {
  return (
    <div
      className={cn(
        'flex items-center justify-end gap-2 px-5 py-3 bg-bg-soft border-t border-border',
        className,
      )}
    >
      {children}
    </div>
  );
}
